import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorCode, getErrorStatusCode } from "../../../../../api/api-response";
import { RestEndpoints } from "../../../../../api/endpoints";
import getInstance from "../../../../../utils/rest";
import { ICfmOrderXBulkUpdateListModel } from "../../../models/order-x/edit/cfm-order-x-bulk-update.model";
import { CfmQueryKeys } from "../../cfm-query-keys";

const bulkUpdateOrderXQuery = async (orderUpdates: ICfmOrderXBulkUpdateListModel): Promise<void> => {
  await getInstance().patch<void>(RestEndpoints.CfmOrdersX, orderUpdates);
  return undefined;
};

export const useBulkUpdateOrderXQuery = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (orderUpdates: ICfmOrderXBulkUpdateListModel) => bulkUpdateOrderXQuery(orderUpdates),

    onError: (error) => {
      const errorMsg = getErrorCode(error);
      const errorCode = getErrorStatusCode(error);
      enqueueSnackbar(t("general.error_occurred", { errorCode, errorMsg }), { variant: "error" });
    },

    onSuccess: () => {
      enqueueSnackbar(t("notifications.order_saved_success.message"), {
        variant: "success",
      });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrdersXWithFilterInfinite] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrdersXWithFilter] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrderXStatusCount] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrderXBulkActions] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrderXDeliveryReport] });
    }
  });
};
